// =============================================================================
// Built-in Bonus Type Definitions
//
// These are static — no DB reads required.  Custom campaign-specific types
// are stored in DynamoDB and merged in at read time by the combat handler.
//
// Stacking rules (D&D 3.5 / Pathfinder 1e):
//   • 'stacks'           — Untyped, Dodge, Circumstance always stack.
//   • 'no_stack_highest' — All other typed bonuses: only the highest applies.
//
// D&D 5e does not use a typed bonus system; 'untyped' and 'circumstance' are
// the only relevant types (advantage/disadvantage is handled separately in
// the roll engine, not as a numeric modifier type).
// =============================================================================
import type { RuleSystem } from '../types/index.js';

export interface BonusTypeDefinition {
  typeId:        string;
  name:          string;
  /** Which game systems recognise this type.  'all' means any RuleSystem. */
  gameSystems:   RuleSystem[] | 'all';
  stackingRule:  'no_stack_highest' | 'stacks';
  notes?:        string;
}

export const BUILT_IN_BONUS_TYPES: readonly BonusTypeDefinition[] = [
  {
    typeId: 'alchemical', name: 'Alchemical',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Bonuses from alchemical sources such as ability-enhancing poisons or mutagens.',
  },
  {
    typeId: 'armor', name: 'Armor',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Granted by armor or the mage armor spell.',
  },
  {
    typeId: 'circumstance', name: 'Circumstance',
    gameSystems: 'all', stackingRule: 'stacks',
    notes: 'Situational modifiers from external conditions. Stacks with all other circumstance bonuses.',
  },
  {
    typeId: 'competence', name: 'Competence',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Improves the ability to perform a specific task, such as from the guidance spell.',
  },
  {
    typeId: 'deflection', name: 'Deflection',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Wards off attacks, increasing AC. Applies to all AC including touch AC.',
  },
  {
    typeId: 'dodge', name: 'Dodge',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'stacks',
    notes: 'Applies to AC. Stacks with all other dodge bonuses. Lost when flat-footed.',
  },
  {
    typeId: 'enhancement', name: 'Enhancement',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Improves an item or natural ability score. Magic weapons and armor grant enhancement bonuses.',
  },
  {
    typeId: 'inherent', name: 'Inherent',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Permanently improves an ability score (e.g. from a wish spell or tome).',
  },
  {
    typeId: 'insight', name: 'Insight',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Grants preternatural clarity of senses.',
  },
  {
    typeId: 'luck', name: 'Luck',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Represents good fortune.',
  },
  {
    typeId: 'morale', name: 'Morale',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Improves performance through resolve, courage, or determination. Does not apply to constructs, undead, or other mindless creatures.',
  },
  {
    typeId: 'natural_armor', name: 'Natural Armor',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Improves natural armor class. Does not apply to touch AC.',
  },
  {
    typeId: 'profane', name: 'Profane',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Granted by the power of evil.',
  },
  {
    typeId: 'resistance', name: 'Resistance',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Improves saving throws.',
  },
  {
    typeId: 'sacred', name: 'Sacred',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Granted by the power of good.',
  },
  {
    typeId: 'shield', name: 'Shield',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Improves AC from a shield or the shield spell. Does not apply to touch AC.',
  },
  {
    typeId: 'size', name: 'Size',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Derived from creature size. Applies to attack rolls, AC, and some skills.',
  },
  {
    typeId: 'exalted', name: 'Exalted',
    gameSystems: ['dnd35', 'pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'Granted by a celestial power as a reward for good deeds.',
  },
  {
    typeId: 'perfection', name: 'Perfection',
    gameSystems: ['pathfinder1'], stackingRule: 'no_stack_highest',
    notes: 'PF1 only. Granted by some ki powers.',
  },
  {
    typeId: 'epic', name: 'Epic',
    gameSystems: ['dnd35'], stackingRule: 'no_stack_highest',
    notes: 'D&D 3.5 only. Granted by epic-level abilities.',
  },
  {
    typeId: 'untyped', name: 'Untyped',
    gameSystems: 'all', stackingRule: 'stacks',
    notes: 'Has no type — always stacks with every other bonus, including other untyped bonuses.',
  },
  {
    typeId: 'proficiency', name: 'Proficiency',
    gameSystems: ['dnd5e'], stackingRule: 'no_stack_highest',
    notes: 'D&D 5e only. Based on character level; applies to proficient skills, saving throws, and attacks.',
  },
] as const;

/** Quick lookup: typeId → definition. */
export const BONUS_TYPE_MAP: Readonly<Record<string, BonusTypeDefinition>> =
  Object.fromEntries(BUILT_IN_BONUS_TYPES.map((t) => [t.typeId, t]));
